import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Stack, Text, TextInput, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';

export function CheckoutUserScreen() {
  const { scanUser, reset } = useKiosk();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Keep input permanently focused so barcode scans always register
  const refocusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    refocusInput();
    window.addEventListener('focus', refocusInput);
    return () => window.removeEventListener('focus', refocusInput);
  }, [refocusInput]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Read directly from DOM — barcode scanners type so fast that
    // React state may not have caught up when Enter is pressed
    const value = inputRef.current?.value?.trim() || inputValue.trim();
    if (value) {
      setInputValue(value);
      scanUser(value);
      // Flash the form card to acknowledge scan
      if (formRef.current) {
        formRef.current.classList.remove('kiosk-scan-flash');
        void formRef.current.offsetWidth;
        formRef.current.classList.add('kiosk-scan-flash');
      }
    }
  };

  return (
    <Stack align="center" gap={40}>
      {/* Badge Scan Area */}
      <Box
        className="kiosk-scan-pulse kiosk-glow"
        style={{
          width: 'min(200px, 50vw)',
          height: 'min(200px, 50vw)',
          position: 'relative',
          borderRadius: 'var(--radius-xl)',
          border: '2px solid var(--cmd-accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--blue-bg)',
          overflow: 'hidden',
        }}
      >
        {/* Corner brackets */}
        <Box style={{ position: 'absolute', top: 12, left: 12, width: 20, height: 20, borderTop: '2px solid var(--cmd-accent)', borderLeft: '2px solid var(--cmd-accent)', borderRadius: '4px 0 0 0', opacity: 0.6 }} />
        <Box style={{ position: 'absolute', top: 12, right: 12, width: 20, height: 20, borderTop: '2px solid var(--cmd-accent)', borderRight: '2px solid var(--cmd-accent)', borderRadius: '0 4px 0 0', opacity: 0.6 }} />
        <Box style={{ position: 'absolute', bottom: 12, left: 12, width: 20, height: 20, borderBottom: '2px solid var(--cmd-accent)', borderLeft: '2px solid var(--cmd-accent)', borderRadius: '0 0 0 4px', opacity: 0.6 }} />
        <Box style={{ position: 'absolute', bottom: 12, right: 12, width: 20, height: 20, borderBottom: '2px solid var(--cmd-accent)', borderRight: '2px solid var(--cmd-accent)', borderRadius: '0 0 4px 0', opacity: 0.6 }} />

        {/* Scanning line */}
        {(
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: '10%',
              right: '10%',
              height: 2,
              backgroundColor: 'var(--cmd-accent)',
              boxShadow: '0 0 16px var(--cmd-accent-glow), 0 0 4px var(--cmd-accent)',
              animation: 'scan-line 2.2s ease-in-out infinite',
              borderRadius: 1,
            }}
          />
        )}

        {/* ID Card Icon */}
        <Box style={{ opacity: 0.8 }}>
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cmd-accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="9" cy="10" r="2" />
            <path d="M15 8h3" />
            <path d="M15 12h3" />
            <path d="M7 16h10" />
          </svg>
        </Box>

      </Box>

      {/* Title and subtitle */}
      <Stack align="center" gap={6}>
        <Title
          order={2}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-primary)',
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
          }}
        >
          Scan Your Badge
        </Title>
        <Text
          ta="center"
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '0.9375rem',
          }}
        >
          Scan your badge or enter your card number
        </Text>
      </Stack>

      {/* Input Group */}
      <Box
        ref={formRef}
        component="form"
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 480,
          padding: 'var(--space-6)',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <Stack gap="md">
          <TextInput
            ref={inputRef}
            size="xl"
            placeholder="Card number"
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onBlur={refocusInput}
            styles={{
              input: {
                textAlign: 'center',
                fontFamily: 'var(--font-mono)',
                fontSize: '1.375rem',
                fontWeight: 500,
                height: 72,
                borderWidth: 1,
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--bg-base)',
                borderColor: 'var(--border-colour)',
                color: 'var(--text-primary)',
                boxShadow: 'var(--shadow-inner)',
                letterSpacing: '0.04em',
                caretColor: 'transparent',
              },
            }}
          />

          <Button
            type="submit"
            size="xl"
            h={72}
            fullWidth
            disabled={!inputValue.trim()}
            styles={{
              root: {
                fontSize: '1.0625rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-lg)',
                fontFamily: 'var(--font-heading)',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                backgroundColor: 'var(--cmd-accent)',
                boxShadow: '0 4px 16px var(--cmd-accent-glow)',
                border: 'none',
                color: 'white',
              },
            }}
          >
            Continue
          </Button>
        </Stack>
      </Box>

      {/* Cancel button */}
      <Button
        variant="subtle"
        size="lg"
        h={64}
        w={200}
        onClick={reset}
        mt={8}
        styles={{
          root: {
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-secondary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-colour)',
            background: 'transparent',
            letterSpacing: '0.02em',
          },
        }}
      >
        Cancel
      </Button>
    </Stack>
  );
}
